import React, { useState, useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import api from '../../services/api';
import { formatearMoneda } from '../../utils/helpers';
import { FaFileInvoice, FaCheckCircle, FaTimesCircle, FaClock, FaEye, FaFilter } from 'react-icons/fa';

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const anioActual = new Date().getFullYear();
const ANIOS = Array.from({ length: 5 }, (_, i) => anioActual - i);

export default function HonorariosPage() {
  const [honorarios,   setHonorarios]   = useState([]);
  const [resumen,      setResumen]      = useState(null);
  const [cargando,     setCargando]     = useState(false);
  const [modalDetalle, setModalDetalle] = useState(null);
  const [imgActiva,    setImgActiva]    = useState(null);
  const [motivo,       setMotivo]       = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroMes,    setFiltroMes]    = useState('');
  const [filtroAnio,   setFiltroAnio]   = useState(anioActual);
  const [filtroF1,     setFiltroF1]     = useState('');
  const [filtroF2,     setFiltroF2]     = useState('');

  useEffect(() => { cargar(); }, []);

  const cargar = async (params = {}) => {
    setCargando(true);
    try {
      const p = new URLSearchParams();
      if (params.estado)       p.append('estado', params.estado);
      if (params.mes)          p.append('mes', params.mes);
      if (params.anio)         p.append('anio', params.anio);
      if (params.fecha_inicio) p.append('fecha_inicio', params.fecha_inicio);
      if (params.fecha_fin)    p.append('fecha_fin', params.fecha_fin);
      const { data } = await api.get(`/honorarios?${p.toString()}`);
      setHonorarios(data.data || []);
      setResumen(data.resumen);
    } catch { setHonorarios([]); }
    finally { setCargando(false); }
  };

  const handleFiltrar = () => cargar({
    estado: filtroEstado, mes: filtroMes, anio: filtroAnio,
    fecha_inicio: filtroF1, fecha_fin: filtroF2,
  });

  const handleLimpiar = () => {
    setFiltroEstado(''); setFiltroMes('');
    setFiltroAnio(anioActual); setFiltroF1(''); setFiltroF2('');
    cargar();
  };

  const handleVerImagen = async (id) => {
    try {
      const response = await api.get(`/honorarios/${id}/archivo`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      setImgActiva(URL.createObjectURL(blob));
    } catch { alert('No hay imagen disponible.'); }
  };

  const handleRevisar = async (id, accion) => {
    if (accion === 'rechazado' && !motivo.trim()) {
      alert('Debes ingresar un motivo de rechazo.'); return;
    }
    try {
      await api.post(`/honorarios/${id}/revisar`, { accion, motivo });
      setModalDetalle(null);
      setMotivo('');
      cargar({ estado: filtroEstado, mes: filtroMes, anio: filtroAnio });
    } catch { alert('Error al procesar la revisión.'); }
  };

  const badgeEstado = (estado) => {
    if (estado === 'aprobado')  return <span className="badge bg-success"><FaCheckCircle className="me-1" />Aprobado</span>;
    if (estado === 'rechazado') return <span className="badge bg-danger"><FaTimesCircle className="me-1" />Rechazado</span>;
    return <span className="badge bg-warning text-dark"><FaClock className="me-1" />Pendiente</span>;
  };

  return (
    <Layout>
      <div className="page-header d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4><FaFileInvoice className="me-2" />Honorarios</h4>
          <p className="text-muted mb-0">Revisión y aprobación de honorarios registrados por contadores</p>
        </div>
      </div>

      {/* KPIs */}
      <div className="row g-3 mb-4">
        {[
          { label: 'Total Bruto',  valor: resumen?.total_bruto,  color: '#2980B9', esMoneda: true },
          { label: 'Pendientes',   valor: resumen?.pendientes,   color: '#F39C12', esMoneda: false },
          { label: 'Aprobados',    valor: resumen?.aprobados,    color: '#27AE60', esMoneda: false },
          { label: 'Rechazados',   valor: resumen?.rechazados,   color: '#E74C3C', esMoneda: false },
        ].map((k, i) => (
          <div className="col-md-3" key={i}>
            <div className="card card-custom h-100">
              <div className="card-body">
                <div className="small text-muted fw-bold mb-1">{k.label}</div>
                <div className="fw-bold fs-5" style={{ color: k.color }}>
                  {k.esMoneda ? formatearMoneda(k.valor) : k.valor}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="card card-custom mb-4">
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-md-2">
              <label className="form-label small fw-bold">Estado</label>
              <select className="form-select form-select-sm" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}>
                <option value="">Todos</option>
                <option value="pendiente">Pendiente</option>
                <option value="aprobado">Aprobado</option>
                <option value="rechazado">Rechazado</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small fw-bold">Mes</label>
              <select className="form-select form-select-sm" value={filtroMes} onChange={e => setFiltroMes(e.target.value)}>
                <option value="">Todos</option>
                {MESES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small fw-bold">Año</label>
              <select className="form-select form-select-sm" value={filtroAnio} onChange={e => setFiltroAnio(e.target.value)}>
                {ANIOS.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small fw-bold">Desde</label>
              <input type="date" className="form-control form-control-sm" value={filtroF1} onChange={e => setFiltroF1(e.target.value)} />
            </div>
            <div className="col-md-2">
              <label className="form-label small fw-bold">Hasta</label>
              <input type="date" className="form-control form-control-sm" value={filtroF2} onChange={e => setFiltroF2(e.target.value)} />
            </div>
            <div className="col-md-2 d-flex gap-2">
              <button className="btn btn-danger btn-sm w-100" onClick={handleFiltrar}>
                <FaFilter className="me-1" /> Filtrar
              </button>
              <button className="btn btn-secondary btn-sm w-100" onClick={handleLimpiar}>Limpiar</button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="card card-custom">
        <div className="card-body">
          <h6 className="fw-bold mb-3">Listado de Honorarios</h6>
          {cargando ? (
            <div className="text-center py-4"><div className="spinner-border text-danger" /></div>
          ) : honorarios.length === 0 ? (
            <div className="text-center py-4 text-muted">No hay honorarios registrados.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-hover">
                <thead className="table-dark">
                  <tr>
                    <th>Prestador</th>
                    <th>RUC/DNI</th>
                    <th>Servicio</th>
                    <th>Categoría</th>
                    <th>Fecha</th>
                    <th className="text-end">Bruto</th>
                    <th className="text-end">Retención</th>
                    <th className="text-end">Neto</th>
                    <th className="text-center">Imagen</th>
                    <th className="text-center">Estado</th>
                    <th className="text-center">Revisar</th>
                  </tr>
                </thead>
                <tbody>
                  {honorarios.map(h => (
                    <tr key={h.id}>
                      <td className="fw-bold">{h.nombre_prestador}</td>
                      <td>{h.ruc_dni}</td>
                      <td style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={h.descripcion_servicio}>{h.descripcion_servicio}</td>
                      <td>
                        {h.categoria_nombre
                          ? <span className="badge" style={{ background: h.categoria_color || '#6c757d' }}>{h.categoria_nombre}</span>
                          : <span className="text-muted">—</span>}
                      </td>
                      <td>{h.fecha_pago}</td>
                      <td className="text-end">{formatearMoneda(h.monto_bruto)}</td>
                      <td className="text-end text-danger">{formatearMoneda(h.monto_retencion)} <small>({h.porcentaje_retencion}%)</small></td>
                      <td className="text-end fw-bold text-success">{formatearMoneda(h.monto_neto)}</td>
                      <td className="text-center">
                        {h.tiene_archivo
                          ? <button className="btn btn-outline-info btn-sm" onClick={() => handleVerImagen(h.id)}><FaEye /></button>
                          : <span className="text-muted">—</span>}
                      </td>
                      <td className="text-center">
                        {badgeEstado(h.estado)}
                        {h.estado === 'rechazado' && h.motivo_rechazo && (
                          <div className="small text-danger mt-1">{h.motivo_rechazo}</div>
                        )}
                      </td>
                      <td className="text-center">
                        {h.estado === 'pendiente' && (
                          <button className="btn btn-outline-primary btn-sm"
                            onClick={() => { setModalDetalle(h); setMotivo(''); }}>
                            <FaEye className="me-1" /> Revisar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="table-dark">
                  <tr>
                    <td colSpan="5" className="fw-bold">TOTAL</td>
                    <td className="text-end fw-bold">{formatearMoneda(resumen?.total_bruto)}</td>
                    <td className="text-end fw-bold text-danger">{formatearMoneda(resumen?.total_retencion)}</td>
                    <td className="text-end fw-bold text-success">{formatearMoneda(resumen?.total_neto)}</td>
                    <td colSpan="3"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal Revisar */}
      {modalDetalle && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.6)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">Revisar Honorario — {modalDetalle.nombre_prestador}</h5>
                <button className="btn-close" onClick={() => setModalDetalle(null)} />
              </div>
              <div className="modal-body">
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <div className="p-3 rounded" style={{ background: '#f8f9fa' }}>
                      <div className="small text-muted">Prestador</div>
                      <div className="fw-bold">{modalDetalle.nombre_prestador}</div>
                      <div className="small text-muted mt-2">RUC/DNI</div>
                      <div>{modalDetalle.ruc_dni}</div>
                      <div className="small text-muted mt-2">Servicio</div>
                      <div>{modalDetalle.descripcion_servicio}</div>
                      <div className="small text-muted mt-2">Fecha de Pago</div>
                      <div>{modalDetalle.fecha_pago}</div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="p-3 rounded" style={{ background: '#f8f9fa' }}>
                      <div className="row g-2 text-center">
                        <div className="col-4">
                          <div className="small text-muted">Bruto</div>
                          <div className="fw-bold text-primary">{formatearMoneda(modalDetalle.monto_bruto)}</div>
                        </div>
                        <div className="col-4">
                          <div className="small text-muted">Retención ({modalDetalle.porcentaje_retencion}%)</div>
                          <div className="fw-bold text-danger">{formatearMoneda(modalDetalle.monto_retencion)}</div>
                        </div>
                        <div className="col-4">
                          <div className="small text-muted">Neto</div>
                          <div className="fw-bold text-success">{formatearMoneda(modalDetalle.monto_neto)}</div>
                        </div>
                      </div>
                      {modalDetalle.tiene_archivo && (
                        <div className="text-center mt-3">
                          <button className="btn btn-outline-info btn-sm" onClick={() => handleVerImagen(modalDetalle.id)}>
                            <FaEye className="me-1" /> Ver imagen del recibo
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label small fw-bold">Motivo de rechazo (requerido si rechaza)</label>
                  <textarea className="form-control form-control-sm" rows={3}
                    value={motivo} onChange={e => setMotivo(e.target.value)}
                    placeholder="Ingresa el motivo si vas a rechazar..." />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setModalDetalle(null)}>
                  Cancelar
                </button>
                <button className="btn btn-danger btn-sm" onClick={() => handleRevisar(modalDetalle.id, 'rechazado')}>
                  <FaTimesCircle className="me-1" /> Rechazar
                </button>
                <button className="btn btn-success btn-sm" onClick={() => handleRevisar(modalDetalle.id, 'aprobado')}>
                  <FaCheckCircle className="me-1" /> Aprobar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Visor Imagen */}
      {imgActiva && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.8)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">Imagen del Recibo</h5>
                <button className="btn-close" onClick={() => setImgActiva(null)} />
              </div>
              <div className="modal-body text-center p-2">
                <img src={imgActiva} alt="recibo" style={{ maxWidth: '100%', maxHeight: '75vh', borderRadius: 8 }} />
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}